import { FunctionComponent, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import { useGetIncomingVehicleIdsMutation } from '../../../api/compoundManagementApi';
import { handleQueryError } from '../../../notifications/ErrorNotification';
import {
    compoundManagementSlice,
    getCompoundManagementModalFilter,
    getVinSearchFilter,
    mapIncomingVehiclesFilterQueryParams,
} from '../../../reducers/compoundManagementSlice';
import { BulkSelectionDropdown } from '../../../sharedComponents/table/BulkSelectionDropdown';
import { FilterMaterializationExceededErrorResponseCodec } from '../../../api/types/compoundManagement/errorCodes';

export const FIXED_INCOMING_CACHE_KEY = 'shared-get-incoming-vehicle-ids';
export const MAX_SELECTION_SIZE = 10_000;

export const IncomingVehicleBulkSelectionDropdown: FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const { getIdsToUpdateSelection, hasTooManyFilterResultsError, resetTooManyFilterResultsError } =
        useGetIncomingVehicleIds();

    const handleAddFilterResultToSelection = async () => {
        const ids = await getIdsToUpdateSelection();
        if (ids !== undefined) {
            dispatch(compoundManagementSlice.actions.addToIncomingBulkSelection(ids));
        }
    };

    const handleRemoveFilterResultFromSelection = async () => {
        const ids = await getIdsToUpdateSelection();
        if (ids !== undefined) {
            dispatch(compoundManagementSlice.actions.removeFromIncomingBulkSelection(ids));
        }
    };

    const handleClearSelection = () => {
        dispatch(compoundManagementSlice.actions.clearIncomingBulkSelection());
    };

    return (
        <BulkSelectionDropdown
            addFilterResultToSelection={handleAddFilterResultToSelection}
            removeFilterResultFromSelection={handleRemoveFilterResultFromSelection}
            clearSelection={handleClearSelection}
            hasTooManyFilterResultsError={hasTooManyFilterResultsError}
            maxSelectionSize={MAX_SELECTION_SIZE}
            resetTooManyFilterResultsError={resetTooManyFilterResultsError}
        />
    );
};

const useGetIncomingVehicleIds = () => {
    const filters = useAppSelector(getCompoundManagementModalFilter);
    const vinSearch = useAppSelector(getVinSearchFilter);

    const [getCompoundVehicleIds] = useGetIncomingVehicleIdsMutation({ fixedCacheKey: FIXED_INCOMING_CACHE_KEY });
    const [hasTooManyFilterResultsError, setHasTooManyFilterResultsError] = useState(false);

    const getIdsToUpdateSelection = async (): Promise<string[] | undefined> => {
        try {
            const filterParams = mapIncomingVehiclesFilterQueryParams(filters, vinSearch);
            return await getCompoundVehicleIds(filterParams).unwrap();
        } catch (error) {
            if (FilterMaterializationExceededErrorResponseCodec.is(error)) {
                setHasTooManyFilterResultsError(true);
            } else {
                handleQueryError(error);
            }
        }
    };

    const resetTooManyFilterResultsError = () => setHasTooManyFilterResultsError(false);

    return { getIdsToUpdateSelection, hasTooManyFilterResultsError, resetTooManyFilterResultsError };
};
